// Ancien abonnement retiré
import { Folder } from '@/types/domain.types';
import { useArchiveFolder, useRestrictFolder } from '@/hooks/useOrganisations';
import { useCurrentMemberRole } from '@/hooks/useCurrentMemberRole';
import { getTemplateDisplayName } from '../constants/automation.constants';
import {
  AUTOMATION_EXECUTION_TONE_CLASSES,
  getAutomationExecutionPresentation,
} from '../utils/automation-execution-presentation';
import {
  Archive,
  ArchiveRestore,
  Calendar,
  CheckCircle,
  CircleAlert,
  CirclePause,
  Clock,
  FolderOpen,
  Lock,
  MoreVertical,
  RefreshCw,
  Server,
  Settings,
  Unlock,
  XCircle,
} from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface FolderItemProps {
  folder: Folder;
  onAutomationClick: (folder: Folder) => void;
  configurationGuard?: ComponentType<{ children: ReactNode }>;
  showDomain?: boolean; // New prop to control domain display
  showCheckbox?: boolean; // New prop to show checkbox for bulk selection
  isSelected?: boolean; // Whether the folder is selected
  onToggleSelection?: (folderUuid: string) => void; // Toggle selection callback
}

const SansGarde = ({ children }: { children: ReactNode }) => <>{children}</>;

export const FolderItem = ({
  folder,
  onAutomationClick,
  configurationGuard: ConfigurationGuard = SansGarde,
  showDomain = false,
  showCheckbox = false,
  isSelected = false,
  onToggleSelection,
}: FolderItemProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { mutate: archiveFolder, isPending: isArchiving } = useArchiveFolder();
  const { mutate: restrictFolder, isPending: isRestricting } = useRestrictFolder();
  const { isAdmin } = useCurrentMemberRole();
  // Ancien modal d'abonnement retiré

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Clean folder name (remove extra whitespace and line breaks)
  const cleanName = folder.name.replace(/\s+/g, ' ').trim();
  const displayName =
    cleanName.length > 50 ? cleanName.substring(0, 50) + '...' : cleanName;

  // Check real automation status
  const hasAutomation =
    folder.automationConfigurations &&
    folder.automationConfigurations.length > 0;
  const hasActiveAutomation =
    hasAutomation &&
    folder.automationConfigurations?.some(config => config.isActive);

  const lastExecution = hasActiveAutomation
    ? folder.lastAutomationExecution
    : null;
  const automationPresentation = lastExecution
    ? getAutomationExecutionPresentation(lastExecution)
    : null;
  const automationStatusLabel = automationPresentation
    ? t(
        automationPresentation.labelKey,
        automationPresentation.labelFallback
      )
    : t('automation.status.idle', 'Aucune exécution');
  const automationToneClasses = automationPresentation
    ? AUTOMATION_EXECUTION_TONE_CLASSES[automationPresentation.tone]
    : AUTOMATION_EXECUTION_TONE_CLASSES.neutral;

  const handleAutomationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    // Ancien abonnement retiré
    onAutomationClick(folder);
  };

  // Get active automation configs for display
  const activeConfigs = folder.automationConfigurations?.filter(c => c.isActive) || [];

  // Short template label for badges
  const getTemplateShortName = (templateId: string): string => {
    switch (templateId) {
      case 'generate-bulletin': return 'Bulletin';
      case 'calculate-dsn': return 'DSN';
      case 'generate-edition': return 'Édition';
      default: return getTemplateDisplayName(templateId);
    }
  };

  return (
    <>
      <div className="group hover:border-primary-200 hover:shadow-primary-100/20 relative overflow-hidden rounded-xl border border-neutral-200 bg-white p-3 shadow-sm transition-all duration-200 hover:shadow-md">
        {/* Subtle gradient background on hover */}
        <div className="from-primary-50/0 via-primary-50/0 to-primary-50/0 group-hover:from-primary-50/30 group-hover:via-primary-50/20 group-hover:to-primary-50/10 absolute inset-0 bg-gradient-to-r transition-all duration-200" />

        <div className="relative flex items-center gap-3">
          {/* Checkbox for bulk selection */}
          {showCheckbox && onToggleSelection && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelection(folder.uuid)}
              onClick={event => event.stopPropagation()}
              className="text-primary-600 focus:ring-primary-500 hover:border-primary-400 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-neutral-300 transition-all duration-200"
            />
          )}

          {/* Folder Icon */}
          <div className="relative">
            <div className="from-primary-50 to-primary-100 group-hover:from-primary-100 group-hover:to-primary-200 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br shadow-sm transition-all duration-200">
              <FolderOpen className="text-primary-600 h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
            </div>
            {/* Automation status indicator */}
            {hasActiveAutomation && (
              <div
                className={`absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border shadow-sm ${automationToneClasses}`}
                title={automationStatusLabel}
                aria-hidden="true"
              >
                {automationPresentation?.icon === 'success' ? (
                  <CheckCircle className="h-2.5 w-2.5" />
                ) : automationPresentation?.icon === 'failure' ? (
                  <XCircle className="h-2.5 w-2.5" />
                ) : automationPresentation?.icon === 'running' ? (
                  <div className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : automationPresentation?.icon === 'retry' ? (
                  <RefreshCw className="h-2.5 w-2.5" />
                ) : automationPresentation?.icon === 'paused' ? (
                  <CirclePause className="h-2.5 w-2.5" />
                ) : automationPresentation ? (
                  <Clock className="h-2.5 w-2.5" />
                ) : (
                  <CirclePause className="h-2.5 w-2.5" />
                )}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
            {/* Left Side - Folder Info */}
            <div className="min-w-0 flex-1">
              {/* Folder Name + Badges */}
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/folders/${folder.uuid}`}
                  className="hover:text-primary-600 cursor-pointer truncate text-left text-sm font-semibold text-neutral-900 transition-colors duration-200"
                  title={`${folder.name} - ${t('folders.clickToView', 'Cliquer pour voir les détails')}`}
                >
                  {displayName}
                </Link>
                {folder.isArchived && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                    <Archive className="h-2.5 w-2.5" />
                    {t('folders.status.archived', 'Archivé')}
                  </span>
                )}
                {folder.isRestricted && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                    <Lock className="h-2.5 w-2.5" />
                    {t('folders.status.restricted', 'Restreint')}
                  </span>
                )}
              </div>

              {/* Info line: Domain + Contact + Manager + Date */}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
                {showDomain && folder.domain && (
                  <div className="flex items-center gap-1">
                    <Server className="h-3 w-3 text-neutral-400" />
                    <span className="font-medium text-neutral-700">
                      {folder.domain.name}
                    </span>
                  </div>
                )}
                {folder.nomContact && (
                  <div className="flex items-center gap-1">
                    <div className="bg-primary-400 h-1 w-1 rounded-full" />
                    <span className="font-medium text-neutral-700">
                      {folder.nomContact}
                    </span>
                  </div>
                )}
                {folder.responsableDossier && (
                  <div className="flex items-center gap-1">
                    <div className="h-1 w-1 rounded-full bg-neutral-300" />
                    <span className="text-primary-700 font-medium">
                      Manager: {folder.responsableDossier}
                    </span>
                  </div>
                )}
                {folder.metadata?.lastModified && (
                  <div className="flex items-center gap-1">
                    <div className="h-1 w-1 rounded-full bg-neutral-300" />
                    <span>Modifié {formatDate(folder.metadata.lastModified)}</span>
                  </div>
                )}
              </div>

              {/* Automation info line */}
              {activeConfigs.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${automationToneClasses}`}
                    aria-label={`${t(
                      'automation.lastExecution',
                      'Dernière exécution'
                    )} : ${automationStatusLabel}`}
                  >
                    {automationPresentation?.tone === 'danger' ? (
                      <CircleAlert aria-hidden="true" className="h-3 w-3" />
                    ) : automationPresentation?.icon === 'retry' ? (
                      <RefreshCw aria-hidden="true" className="h-3 w-3" />
                    ) : automationPresentation?.tone === 'success' ? (
                      <CheckCircle aria-hidden="true" className="h-3 w-3" />
                    ) : (
                      <Clock aria-hidden="true" className="h-3 w-3" />
                    )}
                    <span>{automationStatusLabel}</span>
                  </span>
                  {activeConfigs.map((config) => (
                    <div
                      key={config.uuid}
                      className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5"
                    >
                      <Calendar className="h-3 w-3 text-emerald-600" />
                      <span className="font-medium text-emerald-800">
                        {getTemplateShortName(config.templateId)}
                      </span>
                      <span className="text-emerald-600">
                        le {config.dayOfMonth} à {String(config.executionHour).padStart(2, '0')}h{String(config.executionMinute).padStart(2, '0')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Action Buttons */}
            <div className="flex flex-shrink-0 items-center gap-2">
              <Link
                to={`/folders/${folder.uuid}`}
                className="hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition-all duration-200 hover:shadow-sm"
                title={t('domains.folders.open', 'Open Folder')}
              >
                <FolderOpen className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t('domains.folders.open', 'Open')}
                </span>
              </Link>

              <ConfigurationGuard>
                <button
                  onClick={handleAutomationClick}
                  className="border-primary-200 bg-primary-50 text-primary-700 hover:border-primary-300 hover:bg-primary-100 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:shadow-sm"
                  title={t('automation.configure.button', 'Configure Automation')}
                >
                  <Settings className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">
                    {t('automation.configure.short', 'Automation')}
                  </span>
                </button>
              </ConfigurationGuard>

              {/* Actions Menu - admin/owner only */}
              {isAdmin && (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  disabled={isArchiving || isRestricting}
                  className="inline-flex items-center justify-center rounded-lg border border-neutral-200 bg-white p-1.5 text-neutral-500 transition-colors hover:bg-neutral-50 disabled:opacity-50"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </button>

                {isMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsMenuOpen(false)}
                    />
                    <div className="absolute top-full right-0 z-20 mt-2 w-56 rounded-xl border border-neutral-200 bg-white shadow-lg">
                      <div className="p-2">
                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              archiveFolder({ folderUuid: folder.uuid, archive: !folder.isArchived });
                              setIsMenuOpen(false);
                            }}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                          >
                            {folder.isArchived ? (
                              <>
                                <ArchiveRestore className="h-4 w-4 text-neutral-500" />
                                {t('folders.actions.unarchive', 'Désarchiver')}
                              </>
                            ) : (
                              <>
                                <Archive className="h-4 w-4 text-neutral-500" />
                                {t('folders.actions.archive', 'Archiver')}
                              </>
                            )}
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              restrictFolder({ folderUuid: folder.uuid, isRestricted: !folder.isRestricted });
                              setIsMenuOpen(false);
                            }}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                          >
                            {folder.isRestricted ? (
                              <>
                                <Unlock className="h-4 w-4 text-neutral-500" />
                                {t('folders.actions.unrestrict', 'Libérer l\'accès')}
                              </>
                            ) : (
                              <>
                                <Lock className="h-4 w-4 text-neutral-500" />
                                {t('folders.actions.restrict', 'Restreindre l\'accès')}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Ancien modal d'abonnement retiré */}
    </>
  );
};
